import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

// An in-app confirmation for destructive actions.
//
// These used to call window.confirm. That is fine until the browser decides
// not to show it — embedded webviews and previews suppress native dialogs, and
// a suppressed confirm returns false, which the calling code reads as "the
// user said no". The button then does nothing at all, with no error and no
// message, and the appointment stays on the calendar blocking its slot.
//
// A dialog we render ourselves cannot be suppressed, says more than a native
// one can, and looks like the rest of the app.
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: React.ReactNode;
  confirmLabel: string;
  /** Defaults to "Keep it" — the way out, never the word the action uses. */
  dismissLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  body,
  confirmLabel,
  dismissLabel = 'Keep it',
  busy = false,
  onConfirm,
  onClose,
}) => (
  <Dialog
    open={open}
    onClose={busy ? undefined : onClose}
    maxWidth="xs"
    fullWidth
    aria-labelledby="confirm-dialog-title"
  >
    <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
    <DialogContent>
      {typeof body === 'string' ? <DialogContentText>{body}</DialogContentText> : body}
    </DialogContent>
    <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
      <Button onClick={onClose} disabled={busy} variant="outlined">
        {dismissLabel}
      </Button>
      <Button
        onClick={onConfirm}
        color="error"
        variant="contained"
        disabled={busy}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export default ConfirmDialog;
